import { useState, useEffect, useCallback } from "react";
import { BellRing, Send, RefreshCw, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { listReminders, syncReminders, sendReminderNow, runDueReminders } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
  pending: { label: "Programmato", cls: "bg-slate-800 text-slate-300 border-white/10", Icon: Clock },
  sent: { label: "Inviato", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
  failed: { label: "Fallito", cls: "bg-rose-100 text-rose-800 border-rose-300", Icon: XCircle },
};

export default function RemindersPanel({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(() => listReminders().then(setItems).catch(() => toast.error("Impossibile caricare i richiami")), []);
  useEffect(() => { refresh(); }, [refresh]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(okMsg(res));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setBusy(null);
    }
  };

  const due = items.filter((r) => r.status !== "sent" && new Date(r.due_at) <= new Date()).length;

  return (
    <div data-testid="reminders-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 text-slate-300">
          <BellRing className="h-5 w-5 text-accent" />
          <span className="text-sm">{items.length} richiami · {due} da inviare ora · invio automatico ogni giorno alle 9:00</span>
        </div>
        <div className="flex gap-2">
          <Button data-testid="reminders-sync-button" size="sm" variant="outline" disabled={busy !== null}
            onClick={() => run("sync", syncReminders, (r) => `${r.created} richiami creati dai lead esistenti`)}
            className="gap-2 border-white/10 text-slate-200 hover:bg-slate-800">
            {busy === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sincronizza lead
          </Button>
          {isAdmin && (
            <Button data-testid="reminders-run-due-button" size="sm" disabled={busy !== null}
              onClick={() => run("due", runDueReminders, (r) => `Elaborati ${r.processed}: ${r.sent} inviati, ${r.failed} falliti`)}
              className="gap-2 bg-accent hover:bg-accent/90 text-white">
              {busy === "due" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia scaduti ora
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400">
            <tr>
              <th className="text-left font-medium px-4 py-3">Cliente</th>
              <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Finanziamento</th>
              <th className="text-center font-medium px-4 py-3">Richiamo</th>
              <th className="text-center font-medium px-4 py-3 hidden sm:table-cell">Rinnovabile</th>
              <th className="text-left font-medium px-4 py-3">Stato</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const s = STATUS[r.status] || STATUS.pending;
              const lastErr = (r.log || []).find((l) => !l.ok)?.errore;
              return (
                <tr key={r.id} data-testid={`reminder-row-${r.id}`} className="border-t border-white/5">
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{r.nome || "—"}</div>
                    <div className="text-xs text-slate-500">{r.telefono} {r.email && `· ${r.email}`}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-400 hidden md:table-cell">{r.finanziamento}</td>
                  <td className="px-4 py-3 text-center font-mono text-accent">{r.data_richiamo}</td>
                  <td className="px-4 py-3 text-center font-mono text-slate-300 hidden sm:table-cell">{r.data_rinnovabilita}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded border ${s.cls}`}>
                      <s.Icon className="h-3 w-3" /> {s.label}
                    </span>
                    {lastErr && <div className="text-[11px] text-rose-400 mt-1 max-w-xs truncate" title={lastErr}>{lastErr}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid={`reminder-send-${r.id}`} size="sm" variant="ghost" disabled={busy !== null}
                      onClick={() => run(r.id, () => sendReminderNow(r.id), (res) => res.status === "sent" ? "Richiamo inviato" : "Invio fallito: controlla le impostazioni notifiche")}
                      className="text-slate-300 hover:text-white gap-1">
                      {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia ora
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <BellRing className="h-10 w-10 mx-auto mb-3 opacity-40" />
            Nessun richiamo. Vengono creati automaticamente quando un cliente dichiara finanziamenti in corso.
          </div>
        )}
      </div>
    </div>
  );
}
